import React, { useState, useEffect } from 'react';
import HeroSection from '../components/HeroSection';

function ProposedProjects() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchProjects();
  }, [currentPage]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await fetch(`https://dsdspl.com/mp_funds/public/api/public/projects?status_id=1&page=${currentPage}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      
      const data = await response.json();
      
      if (data.data && Array.isArray(data.data)) {
        setProjects(data.data);
        setTotalPages(data.pagination?.last_page || 1);
      } else {
        setProjects([]);
      }
    } catch (err) {
      setError(err.message);
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCost = (cost) => {
    if (!cost) return '₹ 0';
    const amount = parseFloat(cost);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePageChange = (e) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex items-center justify-between text-sm text-gray-600 py-3">
            <span>Page-05</span>
            <div className="flex items-center gap-2">
              <span>Language</span>
              <select className="border rounded-md px-2 py-1 text-gray-700">
                <option>English</option>
                <option>Hindi</option>
              </select>
            </div>
          </div>
          <div className="text-center py-8">
            <div className="text-lg text-gray-600">Loading projects...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="">
        <HeroSection />
        <div className="mx-auto max-w-6xl px-4">
          <div className="flex items-center justify-between text-sm text-gray-600 py-3">
            <span>Page-05</span>
            <div className="flex items-center gap-2">
              <span>Language</span>
              <select className="border rounded-md px-2 py-1 text-gray-700">
                <option>English</option>
                <option>Hindi</option>
              </select>
            </div>
          </div>
          <div className="text-center py-8">
            <div className="text-lg text-red-600">Error: {error}</div>
            <button 
              onClick={fetchProjects}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-gray-900 text-white px-4 py-2 hover:bg-gray-800"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="">
      {/* Hero banner */}
      <HeroSection />

      <div className="mx-auto max-w-6xl px-4">
        {/* Meta row */}
        <div className="flex items-center justify-between text-sm text-gray-600 py-3">
          <span>Page-05</span>
          <div className="flex items-center gap-2">
            <span>Language</span>
            <select className="border rounded-md px-2 py-1 text-gray-700">
              <option>English</option>
              <option>Hindi</option>
            </select>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl sm:text-3xl font-bold text-center text-green-600 mb-6">Propose Project</h1>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow ring-1 ring-gray-200 overflow-hidden">
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-gray-700">
                  <th className="px-4 py-3 text-left font-semibold border-b">S. No.</th>
                  <th className="px-4 py-3 text-left font-semibold border-b">Project Name</th>
                  <th className="px-4 py-3 text-left font-semibold border-b">Description</th>
                  <th className="px-4 py-3 text-left font-semibold border-b">Location</th>
                  <th className="px-4 py-3 text-left font-semibold border-b">Length/Size</th>
                  <th className="px-4 py-3 text-left font-semibold border-b">Estimated Cost</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {projects.length > 0 ? (
                  projects.map((project, idx) => (
                    <tr key={project.project_id} className={idx % 2 === 1 ? 'bg-gray-50/40' : ''}>
                      <td className="px-4 py-3">{((currentPage - 1) * 15) + idx + 1}.</td>
                      <td className="px-4 py-3 font-medium text-gray-900">{project.project_name}</td>
                      <td className="px-4 py-3">{project.description}</td>
                      <td className="px-4 py-3">{project.location}</td>
                      <td className="px-4 py-3">{project.length_size || 'N/A'}</td>
                      <td className="px-4 py-3">{formatCost(project.estimated_cost)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                      No proposed projects found
                    </td>
                  </tr>
                )}
                
                {/* Empty rows for visual breathing room if there are fewer than 6 projects */}
                {projects.length > 0 && projects.length < 6 && 
                  Array.from({ length: 6 - projects.length }).map((_, i) => (
                    <tr key={`empty-${i}`}>
                      <td className="px-4 py-6 text-transparent">•</td>
                      <td className="px-4 py-6" />
                      <td className="px-4 py-6" />
                      <td className="px-4 py-6" />
                      <td className="px-4 py-6" />
                      <td className="px-4 py-6" />
                    </tr>
                  ))
                }
              </tbody>
            </table>
          </div>

          {/* Table footer controls */}
          <div className="flex items-center justify-between px-4 py-4 border-t bg-gray-50">
            <button 
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
              className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 ${
                currentPage >= totalPages 
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              Next <span className="text-lg">→</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <span>Pages</span>
              <input 
                className="w-12 border rounded-md px-2 py-1 text-center" 
                value={currentPage}
                onChange={handlePageChange}
                type="number"
                min="1"
                max={totalPages}
              />
              <span>of {totalPages}</span>
            </div>
          </div>
        </div>

        {/* Note or CTA */}
        <div className="text-center text-xs text-gray-500 mt-4 mb-2">
          Showing {projects.length} proposed projects
        </div>
      </div>
    </div>
  );
}

export default ProposedProjects;